import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRightIcon, SunIcon, MoonIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { TenantBrandMark } from '../components/common/TenantBrandMark';
import { TenantFooter } from '../components/common/TenantFooter';
import { TenantBackdrop } from '../components/common/TenantBackdrop';
import { useTenant } from '../contexts/TenantContext';
import { api, getBrand } from '../config/api';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedNext = searchParams.get('next');
  const destination = requestedNext && /^\/inbde(?:\/|\?|$)/.test(requestedNext)
    ? requestedNext : getBrand() === 'inbde' ? '/inbde' : '/dashboard';
  const { signup, googleLogin } = useAuth();
  const { tenant } = useTenant();
  const hasTenantCover = Boolean(tenant?.cover_image_url);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode] = useState(() => searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState<boolean | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Update document class when theme changes
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    // Validate referral code if present
    if (referralCode) {
      api.get(`/api/referral/validate?code=${encodeURIComponent(referralCode)}`)
        .then(res => setReferralValid(res.data.valid))
        .catch(() => setReferralValid(false));
    }
  }, [referralCode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, fullName || undefined, referralCode || undefined);
      navigate(destination);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setError('');
    setLoading(true);

    try {
      await googleLogin(credentialResponse.credential, referralCode || undefined);
      navigate(destination);
    } catch (err: any) {
      // Surface the backend's detail (e.g. tenant-membership 403:
      // "You are not a member of JSPT.") so the user knows whether
      // they need an invite, not just that "something failed".
      setError(err.response?.data?.detail || 'Failed to sign up with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden ${
      isDarkMode ? 'bg-[#030712]' : 'bg-gray-50'
    }`}>
      <TenantBackdrop isDarkMode={isDarkMode} />

      {/* Vidya gradient orbs — platform / no-cover-image fallback only.
          When a tenant cover is rendered, TenantBackdrop owns the visuals. */}
      {isDarkMode && !hasTenantCover && (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/10 via-blue-900/10 to-slate-900/10"></div>
          {/* Floating orbs */}
          <div
            className="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 60%)',
              filter: 'blur(60px)',
            }}
          />
          <div
            className="absolute bottom-1/3 left-1/4 w-[250px] h-[250px] rounded-full opacity-15"
            style={{
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 60%)',
              filter: 'blur(60px)',
            }}
          />
        </div>
      )}

      {/* Theme toggle button */}
      <button
        onClick={toggleTheme}
        className={`absolute top-8 right-8 p-2 rounded-lg transition-colors ${
          isDarkMode
            ? 'text-slate-400 hover:text-white hover:bg-slate-800'
            : 'text-gray-600 hover:text-gray-900 hover:bg-white'
        }`}
        aria-label="Toggle theme"
      >
        {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
      </button>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <Link to="/" className="block">
          <TenantBrandMark logoSize={44} isDarkMode={isDarkMode} />
        </Link>
        <h2 className={`mt-6 text-center text-2xl font-bold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Create your account
        </h2>
        <p className={`mt-2 text-center text-sm ${
          isDarkMode ? 'text-slate-400' : 'text-gray-600'
        }`}>
          Already have an account?{' '}
          <Link to={destination === '/dashboard' ? '/login' : `/login?next=${encodeURIComponent(destination)}`} className={`font-medium transition-colors ${
            isDarkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
          }`}>
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className={`py-8 px-4 shadow-xl rounded-2xl sm:px-10 ${
          isDarkMode 
            ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-xl' 
            : 'bg-white border border-gray-200'
        }`}
        style={isDarkMode ? {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
        } : {}}
        >
          {referralCode && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${
              referralValid === true
                ? isDarkMode
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                  : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : referralValid === false
                ? isDarkMode
                  ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
                  : 'bg-amber-50 border border-amber-200 text-amber-700'
                : isDarkMode
                  ? 'bg-slate-700/50 border border-slate-600 text-slate-300'
                  : 'bg-gray-50 border border-gray-200 text-gray-600'
            }`}>
              {referralValid === true ? (
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
              ) : referralValid === false ? (
                <XCircleIcon className="h-5 w-5 flex-shrink-0" />
              ) : null}
              <span>
                {referralValid === true
                  ? `Referred via ${referralCode.toUpperCase()}`
                  : referralValid === false
                  ? `Referral code "${referralCode.toUpperCase()}" is invalid or expired`
                  : `Checking referral code...`}
              </span>
            </div>
          )}

          {error && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
              isDarkMode
                ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                : 'bg-red-50 border border-red-200 text-red-600'
            }`}>
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="fullName" className={`block text-sm font-medium ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Full Name (Optional)
              </label>
              <div className="mt-1">
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  className={`appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={`appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className={`block text-sm font-medium ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className={`appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                  boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.boxShadow = '0 0 50px rgba(6, 182, 212, 0.5)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span className="flex items-center">
                  {loading ? 'Creating account...' : 'Create account'}
                  {!loading && <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                </span>
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-300'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${
                  isDarkMode ? 'bg-slate-900/50 text-slate-400' : 'bg-white text-gray-500'
                }`}>
                  {tenant ? `Or join ${tenant.name} with` : 'Or sign up with'}
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google sign up failed')}
                useOneTap
              />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 z-10">
        <TenantFooter isDarkMode={isDarkMode} className="border-0" />
      </div>
    </div>
  );
};

export default Signup;
